import { z } from 'zod';
import { pageSchema, timestampSchema } from './common';

/**
 * Operational annotations.
 *
 * The record an automation leaves behind when it observes something worth
 * saying but has nothing to change. Kept separate from the audit log on
 * purpose: audit answers "who did what to the system", annotations answer
 * "what was the system telling us at the time". Mixing them makes the audit log
 * unusable for its actual job, which is investigating access.
 *
 * Unlike audit records, annotations are ordinary data -- they can be
 * acknowledged and they expire under a retention policy.
 */

export const annotationSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type AnnotationSeverity = z.infer<typeof annotationSeveritySchema>;

export const annotationSourceSchema = z.enum(['automation', 'integration', 'operator']);

export const annotationSchema = z.object({
  id: z.string().uuid(),
  severity: annotationSeveritySchema,
  message: z.string(),
  source: annotationSourceSchema,
  /** The automation or integration that produced it, when there was one. */
  sourceId: z.string().uuid().nullable(),
  sourceLabel: z.string().nullable(),
  /** What it is about, so the dashboard can show it next to that thing. */
  subjectType: z.string().nullable(),
  subjectId: z.string().nullable(),
  acknowledgedAt: timestampSchema.nullable(),
  acknowledgedByUserId: z.string().uuid().nullable(),
  createdAt: timestampSchema,
});
export type Annotation = z.infer<typeof annotationSchema>;

export const annotationPageSchema = pageSchema(annotationSchema);

export const annotationListQuerySchema = z.object({
  severity: annotationSeveritySchema.optional(),
  acknowledged: z.enum(['true', 'false']).optional(),
  subjectType: z.string().max(64).optional(),
  subjectId: z.string().max(64).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export const createAnnotationSchema = z
  .object({
    severity: annotationSeveritySchema,
    message: z.string().min(1).max(500),
    subjectType: z.string().max(64).nullish(),
    subjectId: z.string().max(64).nullish(),
  })
  .strict();
export type CreateAnnotation = z.infer<typeof createAnnotationSchema>;
